import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import { Skeleton } from "@mui/material";
// import BackIcon from "@styles/all-images/icons/back-button.svg";

interface Profile {
  id: number;
  name: string;
  is_archive: boolean;
}

interface StaffManagementEditCardProps {
  onClose: () => void;
  onSubmit: () => void;
  staffId?: number;
  selectedProfiles?: number[];
  selectedCompanyId: number;
}

const StaffManagementEditCard = ({
  onClose,
  onSubmit,
  staffId = 0,
  selectedProfiles = [],
  selectedCompanyId,
}: StaffManagementEditCardProps) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfileIds, setSelectedProfileIds] =
    useState<number[]>(selectedProfiles);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(true);

  useEffect(() => {
    const fetchProfiles = async () => {
      setIsLoadingProfiles(true);
      try {
        const { authenticatedCall } = actionsHandler(
          "permissionProfilesListByCompanyId",
        );
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [selectedCompanyId.toString() + "/"],
          }),
        );

        if (response.ok) {
          const activeProfiles = response.data.filter(
            (profile: Profile) => !profile.is_archive,
          );
          setProfiles(activeProfiles);
        } else {
          toast.error("Failed to fetch profiles");
        }
      } catch (error) {
        console.error("Error fetching profiles:", error);
        toast.error("Unable to load profiles");
      } finally {
        setIsLoadingProfiles(false);
      }
    };

    fetchProfiles();
  }, [dispatch]);

  const handleProfileChange = (profileId: number) => {
    setSelectedProfileIds((prev) =>
      prev.includes(profileId)
        ? prev.filter((id) => id !== profileId)
        : [...prev, profileId],
    );
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("userAssignedProfile");
      const response = await dispatch(
        authenticatedCall({
          method: "put",
          payload: {
            profile_ids: selectedProfileIds,
          },
          customParams: [`${staffId.toString()}/`],
        }),
      );

      if (response.ok) {
        toast.success(response.data?.message);
        onSubmit();
      } else {
        toast.error(response.data?.message);
      }
    } catch (error) {
      console.error("Error updating profiles:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
        <div className="tw-bg-white tw-rounded-2xl tw-w-80 tw-p-6">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
            <div></div>
            <h2 className="tw-text-lg font-weight-semibold font-primary-color ">
              Edit Profile
            </h2>
            <button
              onClick={onClose}
              className="tw-text-gray-500 tw-hover:text-gray-700"
            >
              <img src={CloseIcon} alt="Close Icon" className="tw-w-5 tw-h-5" />
            </button>
          </div>

          <div className="tw-space-y-4 tw-mb-6 tw-max-h-[300px] tw-overflow-y-auto tw-pr-2">
            {isLoadingProfiles
              ? Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="tw-flex tw-items-center tw-space-x-3"
                  >
                    <Skeleton
                      variant="rectangular"
                      width={16}
                      height={16}
                      className="tw-rounded"
                    />
                    <Skeleton variant="text" width={150} height={24} />
                  </div>
                ))
              : profiles.map((profile) => (
                  <label
                    key={profile.id}
                    className="tw-flex tw-items-center tw-space-x-3 tw-cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProfileIds.includes(profile.id)}
                      onChange={() => handleProfileChange(profile.id)}
                      className="tw-w-4 tw-h-4 tw-rounded tw-border-gray-300"
                    />
                    <span className="tw-text-sm font-weight-base font-primary-color tw-truncate tw-max-w-[200px] tw-block tw-overflow-hidden">
                      {profile.name}
                    </span>
                  </label>
                ))}
          </div>

          <div className="tw-flex tw-space-x-4">
            <button
              onClick={onClose}
              className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
            >
              {isLoading ? "Updating..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffManagementEditCard;
